/**
 * Wave-4 / A8 — Engine fallback supervisor.
 *
 * Pure function that evaluates the current Giro3D scene-item health snapshot
 * and decides whether a one-click switch to Potree should be offered. See
 * `docs/design-briefs/A8-engine-routing-fallback.md`.
 *
 * Trigger: `currentEngine === 'giro3d'`, the snapshot contains at least one
 * `health === 'unsupported'` COPC SceneItem, AND a Potree-v2 alternate exists
 * in the same `sourceGroupId` (or is nominated through
 * `extensions.pointcloud.alternateSceneItemIds`). Otherwise returns `null`.
 *
 * The function is side-effect free; the host renders the proposal through the
 * existing `<Banner severity="warning" />` primitive.
 */

import type { SceneSlot } from '@/sdk-stub/gtwin-sdk';
import type { ScenePackageDocument } from './scenePackage';
import type { RoutedEngineId } from './engineRouting';

/** Health value the supervisor cares about. Matches `SceneItemHealth` from the Giro3D adapter. */
export type SceneItemHealthInput = 'ready' | 'unsupported' | 'failed' | 'pending';

/**
 * Minimal scene-item health record. The Giro3D adapter's
 * `CopcSceneItemSnapshot` is a structural superset; callers can pass it
 * directly without translation.
 */
export interface SceneItemHealthRecord {
  readonly layerId: string;
  readonly health: SceneItemHealthInput;
  readonly sourceGroupId?: string | null;
}

export interface FallbackProposal {
  readonly proposedEngine: RoutedEngineId;
  readonly proposedSceneItemIds: string[];
  readonly message: string;
  /** Layer ids that triggered the proposal (one per affected COPC SceneItem). */
  readonly affectedSceneItemIds: string[];
}

type JsonObject = Record<string, unknown>;

interface NormalizedPackageItem {
  readonly id: string;
  readonly slot: SceneSlot;
  readonly artifactKind: string | null;
  readonly sourceGroupId: string | null;
  readonly alternateSceneItemIds: ReadonlyArray<string>;
  readonly defaultSceneItemId: string | null;
}

/**
 * Evaluate the current scene state and propose a Potree fallback when an
 * unsupported COPC SceneItem has a viable Potree alternate in the same
 * `sourceGroupId`. Returns `null` when no proposal applies.
 */
export function evaluateFallback(
  currentEngine: RoutedEngineId,
  sceneHealth: ReadonlyArray<SceneItemHealthRecord>,
  scenePackage: ScenePackageDocument | null | undefined,
): FallbackProposal | null {
  if (currentEngine !== 'giro3d') return null;
  if (sceneHealth.length === 0) return null;
  if (!scenePackage) return null;

  const unsupported = sceneHealth.filter((entry) => entry.health === 'unsupported');
  if (unsupported.length === 0) return null;

  const items = collectItems(scenePackage);
  if (items.length === 0) return null;

  const itemsById = new Map(items.map((item) => [item.id, item] as const));
  const potreeByGroup = groupPotreeIdsBySourceGroup(items);

  const proposedIds = new Set<string>();
  const affectedIds: string[] = [];

  for (const entry of unsupported) {
    const item = itemsById.get(entry.layerId);
    const groupId = (entry.sourceGroupId ?? item?.sourceGroupId) ?? null;

    let candidates: string[] = [];
    if (groupId) {
      candidates = potreeByGroup.get(groupId) ?? [];
    }
    // Also honor explicit `alternateSceneItemIds` on the failing item — they
    // can nominate a Potree alternate outside the declared `sourceGroupId`.
    if (item) {
      for (const altId of item.alternateSceneItemIds) {
        const candidate = itemsById.get(altId);
        if (candidate?.artifactKind === 'potree-v2-octree') {
          candidates = candidates.concat(altId);
        }
      }
    }

    if (candidates.length === 0) continue;
    affectedIds.push(entry.layerId);
    for (const candidate of candidates) proposedIds.add(candidate);
  }

  if (affectedIds.length === 0 || proposedIds.size === 0) return null;

  const proposed = [...proposedIds];
  const packageDefault = readPackageDefault(scenePackage);
  if (packageDefault && proposed.includes(packageDefault)) {
    proposed.splice(proposed.indexOf(packageDefault), 1);
    proposed.unshift(packageDefault);
  }

  const message =
    affectedIds.length === 1
      ? 'A point-cloud layer is unsupported on Giro3D. Switch to Potree to view its alternate.'
      : `${affectedIds.length} point-cloud layers are unsupported on Giro3D. Switch to Potree to view their alternates.`;

  return {
    proposedEngine: 'potree',
    proposedSceneItemIds: proposed,
    message,
    affectedSceneItemIds: affectedIds,
  };
}

// ---------------------------------------------------------------------------
// Internal helpers (kept private — mirror engineRouting's normalize shape)
// ---------------------------------------------------------------------------

function collectItems(scenePackage: ScenePackageDocument): NormalizedPackageItem[] {
  if (!isObject(scenePackage.slots)) return [];
  const out: NormalizedPackageItem[] = [];
  const slots = scenePackage.slots as Record<string, unknown>;
  for (const slotName of Object.keys(slots)) {
    const slot = slotName as SceneSlot;
    const raw = slots[slotName];
    const items = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? [raw] : [];
    for (const entry of items) {
      if (!isObject(entry)) continue;
      const normalized = normalizeItem(entry, slot);
      if (normalized) out.push(normalized);
    }
  }
  return out;
}

function normalizeItem(raw: JsonObject, slot: SceneSlot): NormalizedPackageItem | null {
  const id = typeof raw.id === 'string' && raw.id.length > 0 ? raw.id : null;
  if (!id) return null;
  const artifactKind = typeof raw.artifactKind === 'string' ? raw.artifactKind : null;
  const ext = isObject(raw.extensions) ? raw.extensions : undefined;
  const pointcloudExt = ext && isObject(ext.pointcloud) ? ext.pointcloud : undefined;
  const sourceGroupId =
    pointcloudExt && typeof pointcloudExt.sourceGroupId === 'string' && pointcloudExt.sourceGroupId.length > 0
      ? pointcloudExt.sourceGroupId
      : null;
  const alternateSceneItemIds = pointcloudExt && Array.isArray(pointcloudExt.alternateSceneItemIds)
    ? pointcloudExt.alternateSceneItemIds.filter((value): value is string => typeof value === 'string')
    : [];
  const defaultSceneItemId =
    pointcloudExt && typeof pointcloudExt.defaultSceneItemId === 'string'
      ? pointcloudExt.defaultSceneItemId
      : null;
  return { id, slot, artifactKind, sourceGroupId, alternateSceneItemIds, defaultSceneItemId };
}

function groupPotreeIdsBySourceGroup(items: ReadonlyArray<NormalizedPackageItem>): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const item of items) {
    if (item.artifactKind !== 'potree-v2-octree') continue;
    if (!item.sourceGroupId) continue;
    const list = map.get(item.sourceGroupId) ?? [];
    list.push(item.id);
    map.set(item.sourceGroupId, list);
  }
  return map;
}

function readPackageDefault(scenePackage: ScenePackageDocument | null | undefined): string | null {
  if (!scenePackage) return null;
  const ext = (scenePackage as { extensions?: unknown }).extensions;
  if (!isObject(ext)) return null;
  const pointcloud = isObject(ext.pointcloud) ? ext.pointcloud : null;
  if (!pointcloud) return null;
  return typeof pointcloud.defaultSceneItemId === 'string' ? pointcloud.defaultSceneItemId : null;
}

function isObject(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
